import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import {
    BillingAddressElement,
    PaymentElement,
    useCheckout,
} from '@stripe/react-stripe-js/checkout'
import {
    clearActiveCheckout,
    setActiveCheckout,
} from '../checkoutSessionRuntime'
import PaymentFormSkeleton from './PaymentFormSkeleton'

// CON-9505. Every payment option lives in this one element: card, Apple Pay, Google Pay, Amazon Pay and
// the buyer's saved cards, all as rows in its accordion. Which rows appear is decided by the session's
// payment method configuration, server-side — element options can only narrow that, never add to it.
//
// Logo then label, no radios. react-stripe-js re-sends every option through update() on the first
// render and update() rejects the layout keys that are create-only, so this list stays minimal.
const LAYOUT = {
    type: 'accordion',
    defaultCollapsed: false,
    radios: 'never',
    spacedAccordionItems: true,
}

// No fields override: the billing address element below owns billing entry, and Stripe hides the payment
// element's own billing fields while it is mounted, so nothing is asked for twice.
// address 'never' is PGC's: the element otherwise collects billing itself whenever the
// BillingAddressElement below is unmounted, which is now the case for a saved card. Other methods and
// saved cards supply their own address.
const PAYMENT_ELEMENT_OPTIONS = {
    layout: LAYOUT,
    terms: { card: 'never' },
    fields: { billingDetails: { address: 'never' } },
}

// PGC's option: without it the element's name field is Stripe's default, and confirm can reject the
// billing contact for a missing name with no field on screen to fix it.
const BILLING_ADDRESS_OPTIONS = { display: { name: 'full' } }

const CheckoutSessionFields = forwardRef(function CheckoutSessionFields(
    {
        onFocus,
        onSelectionChange,
        onLoadError,
        onInteract,
        onElementReady,
        // Rendered here, not by the caller, so it lands above the billing address.
        payPalRow,
        payPalOpen,
    },
    ref
) {
    const checkoutState = useCheckout()
    const [ready, setReady] = useState(false)
    // Drives the billing address mount. A saved card carries its own billing details, so collecting
    // them again is what GY and PGC both avoid.
    const [selection, setSelection] = useState(null)

    // The forms gate the skeleton and the PayPal row on this signal so every payment option appears
    // in one paint. Reported false on unmount: a session re-create re-keys the provider, and the
    // replacement element has its own load to wait out.
    useEffect(() => () => onElementReady?.(false), [onElementReady])

    // A session that cannot load still reports settled: the buyer keeps the PayPal path, and the
    // skeleton must not sit over the error message.
    useEffect(() => {
        if (checkoutState.type === 'error') {
            onElementReady?.(true)
        }
    }, [checkoutState, onElementReady])

    // The mounted Payment Element instance, so the hand-built PayPal row can collapse the Stripe rows
    // when it opens — PGC's lesson: do it through an imperative handle, synchronously in the click
    // handler, or the first click collapses nothing visually.
    const elementRef = useRef(null)

    useImperativeHandle(ref, () => ({
        collapse: () => elementRef.current?.collapse(),
    }))

    // This component is the only place inside the provider tree, so it is what publishes the mounted
    // checkout instance outwards: submit needs it to confirm, and a total change needs it to run the
    // server update through the element rather than around it.
    useEffect(() => {
        if (checkoutState.type !== 'success') {
            return undefined
        }

        const checkout = checkoutState.checkout
        setActiveCheckout(checkout)

        return () => clearActiveCheckout(checkout)
    }, [checkoutState])

    // The element fires one change of its own shortly after it loads. That synthetic event reports the
    // row it opened on, which is worth recording, but it is not the buyer choosing anything — so it must
    // not be reported outwards as a selection.
    const settled = useRef(false)

    const handleChange = (event) => {
        // A collapsed element has no row open, so there is nothing selected to collect for.
        const value = event.collapsed ? null : event.value

        // Recorded before the guard, as GY does: the synthetic first event is what reports the row the
        // element opened on, and the address mount below depends on knowing it.
        setSelection(value)

        if (!settled.current) {
            settled.current = true
            return
        }

        // The element lives in an iframe, so a click on one of its rows never reaches the wrapper's
        // mousedown — this change event is the only signal that the buyer picked a Stripe row, and it
        // is what closes the PayPal row. Guarded on collapsed, PGC's lesson: opening PayPal collapses
        // this element programmatically, which fires a change too, and reporting THAT as interaction
        // would close PayPal in the same breath as opening it.
        if (!event.collapsed) {
            onInteract?.()
        }

        onSelectionChange?.(value)
    }


    if (checkoutState.type === 'error') {
        // The row survives the error: PayPal is the only way left to pay.
        return (
            <React.Fragment>
                <div role="alert">
                    {checkoutState.error?.message ||
                        'We could not load the payment form. Please refresh and try again.'}
                </div>
                {payPalRow}
            </React.Fragment>
        )
    }

    if (checkoutState.type !== 'success') {
        return <PaymentFormSkeleton />
    }

    return (
        <React.Fragment>
            {!ready && <PaymentFormSkeleton />}
            {/* Belt and braces for clicks that land outside the iframe (its surrounding padding);
                clicks inside it are caught by handleChange above instead. */}
            <div onMouseDown={() => onInteract?.()}>
                <PaymentElement
                    options={PAYMENT_ELEMENT_OPTIONS}
                    onFocus={onFocus}
                    onReady={(paymentElement) => {
                        elementRef.current = paymentElement
                        setReady(true)
                        onElementReady?.(true)
                    }}
                    onChange={handleChange}
                    onLoadError={(event) => onLoadError?.(event)}
                />
            </div>

            {ready && payPalRow}

            {/* Only while entering a new card, matching GY and PGC. Unmounted rather than hidden:
                PGC found a display:none BillingAddressElement never lays out and stays stuck at a
                near-zero height even once shown. */}
            {ready &&
                selection?.type === 'card' &&
                !selection?.payment_method &&
                !payPalOpen && (
                    <BillingAddressElement
                        options={BILLING_ADDRESS_OPTIONS}
                        onFocus={onFocus}
                    />
                )}
        </React.Fragment>
    )
})

// The gate lives here rather than at each call site. useCheckout() runs before any check the component
// could make for itself, so rendering this without CheckoutElementsProvider above it throws on sight —
// and the provider is absent for real reasons: the session is still being created on first paint, and a
// redirect return has no session at all. Keeping the guard in one place means a caller cannot forget it.
const CheckoutSessionPaymentFields = forwardRef(function CheckoutSessionPaymentFields(
    { checkoutSessionReady, checkoutSessionLoadFailed, ...props },
    ref
) {
    if (!checkoutSessionReady) {
        // No session and none coming — creating one failed. Blank space here reads as a page that
        // never finished loading; a refresh genuinely starts over, so say that.
        if (checkoutSessionLoadFailed) {
            return (
                <React.Fragment>
                    <div role="alert">
                        We could not load the payment form. Please refresh and
                        try again.
                    </div>
                    {props.payPalRow}
                </React.Fragment>
            )
        }

        // The session is still being created; the same skeleton the element phase shows, so the
        // create round trip and the element load read as one continuous wait.
        return <PaymentFormSkeleton />
    }

    return <CheckoutSessionFields ref={ref} {...props} />
})

export default CheckoutSessionPaymentFields
